"use client"

import type { FC, ReactNode } from "react"
import { Fragment, useEffect, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconChevronExpandYOutline18 as ChevronsUpDown,
} from "nucleo-ui-outline-18"

import type { PortalApiReferencePage } from "@productlane/api/src/lib/portal-api-reference"
import type {
	CodeSamples,
	OperationDoc,
	ResponseDoc,
} from "@productlane/lib/src/api-reference"
import { schemaTypeLabel } from "@productlane/lib/src/api-reference/schema-tree"
import { cn } from "@productlane/lib/src/cn"

import type { CodeLanguage } from "./highlighted-code"
import { Badge } from "../badge"
import { Button } from "../button"
import { CopyButton } from "../copy-button"
import { DropdownMenu, DropdownMenuItem } from "../internal/dropdown"
import { useApiReferenceEnv, usePointerClass } from "./env"
import { exampleLanguage, HighlightedCode } from "./highlighted-code"
import { INLINE_CODE_CLASS, InlineMarkdown } from "./inline-markdown"
import { ApiMethodBadge } from "./method-badge"
import { AuthFields, ParamFields, SchemaFields } from "./schema-tree"

const SAMPLE_HIGHLIGHT: Record<keyof CodeSamples, CodeLanguage> = {
	curl: "bash",
	javascript: "javascript",
	python: "python",
}

export const SAMPLE_LANGUAGES: { id: keyof CodeSamples; label: string }[] = [
	{ id: "curl", label: "cURL" },
	{ id: "javascript", label: "JavaScript" },
	{ id: "python", label: "Python" },
]

const LANGUAGE_STORAGE_KEY = "productlane.apiReference.language"

const PARAM_ORDER = ["path", "query", "header", "cookie"] as const

/** One page of an API reference: its landing page, or one endpoint or event. */
export const ApiReferencePageView: FC<{
	page: PortalApiReferencePage
	className?: string
}> = ({ page, className }) =>
	page.operation ? (
		<OperationView
			page={page}
			operation={page.operation}
			className={className}
		/>
	) : (
		<OverviewView page={page} className={className} />
	)

/** The server's own path prefix, e.g. `/api/v2` from `https://productlane.com/api/v2`. */
export function serverPathPrefix(server: string | undefined): string {
	return (server ?? "")
		.replace(/^[a-z][a-z\d+.-]*:\/\/[^/]+/i, "")
		.replace(/\/+$/, "")
}

const Section: FC<{
	title: string
	aside?: ReactNode
	children: ReactNode
}> = ({ title, aside, children }) => (
	<section className="flex flex-col">
		<div className="flex min-h-9 items-center justify-between gap-3 border-b border-gray-200/80 pb-2.5 dark:border-white/[0.08]">
			<h2 className="text-[16px] font-semibold tracking-[-0.011em] text-primary">
				{title}
			</h2>
			{aside && <div className="flex items-center gap-2">{aside}</div>}
		</div>
		{children}
	</section>
)

/** The method as a colored code badge, or EVENT for a webhook. */
const MethodBadge: FC<{ operation: OperationDoc }> = ({ operation }) => (
	<ApiMethodBadge method={operation.method} kind={operation.kind} />
)

const PathText: FC<{ path: string }> = ({ path }) => (
	<>
		{path
			.split("/")
			.filter(Boolean)
			.map((segment, index) => (
				<Fragment key={index}>
					<span className="px-[3px] text-tertiary">/</span>
					{segment.split(/(\{[^}]+\})/).map((part, partIndex) =>
						/^\{[^}]+\}$/.test(part) ? (
							<code
								key={partIndex}
								className={cn(INLINE_CODE_CLASS, "text-[1em] text-secondary")}
							>
								{part}
							</code>
						) : (
							part
						),
					)}
				</Fragment>
			))}
	</>
)

const UrlBar: FC<{ copyValue: string; children: ReactNode }> = ({
	copyValue,
	children,
}) => (
	<div className="flex h-11 items-center gap-3 rounded-xl bg-white pl-2.5 pr-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.08] dark:bg-white/[0.03] dark:shadow-none dark:ring-white/[0.09]">
		<div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto whitespace-nowrap font-mono text-[13px] text-primary [scrollbar-width:none]">
			{children}
		</div>
		<CopyButton
			value={copyValue}
			className="text-tertiary hover:text-primary"
		/>
	</div>
)

const OperationView: FC<{
	page: PortalApiReferencePage
	operation: OperationDoc
	className?: string
}> = ({ page, operation, className }) => {
	const { t, Link, monoClassName } = useApiReferenceEnv()
	const isWebhook = operation.kind === "webhook"
	const url = `${page.servers[0] ?? ""}${operation.path}`
	const groupName =
		page.groups.find((group) => group.slug === operation.groupSlug)?.name ??
		t("apiReference.webhookEvents", "Webhook events")
	const okResponse = operation.responses.find((r) => r.status.startsWith("2"))
	const [status, setStatus] = useState(
		okResponse?.status ?? operation.responses[0]?.status ?? null,
	)
	const params = PARAM_ORDER.flatMap((location) =>
		operation.params.filter((p) => p.in === location),
	)
	const examples = operation.responses.filter((r) => r.example)

	// Phones read top to bottom, so each card sits beside the section it shows; from xl it moves to the sticky column.
	const samplesCard = operation.samples ? (
		<SamplesCard samples={operation.samples} operation={operation} />
	) : null
	const payloadCard =
		isWebhook && operation.body?.example ? (
			<CodeCard
				header={
					<span className="text-[12.5px] font-medium text-secondary">
						{t("apiReference.examplePayload", "Example payload")}
					</span>
				}
				actions={
					<CopyButton
						value={operation.body.example}
						className="text-tertiary hover:text-primary"
					/>
				}
			>
				<HighlightedCode
					code={operation.body.example}
					language={exampleLanguage(operation.body.example)}
					className="max-h-[32rem]"
				/>
			</CodeCard>
		) : null
	const responseCard =
		!isWebhook && examples.length > 0 ? (
			<ResponseExampleCard
				examples={examples}
				status={status}
				onStatusChange={setStatus}
			/>
		) : null

	return (
		<div
			className={cn(
				"mx-auto flex w-full min-w-0 max-w-[1180px] flex-col pb-16 pt-6",
				monoClassName,
				className,
			)}
		>
			<div className="flex w-full flex-col gap-12 xl:flex-row xl:items-start">
				<article className="flex min-w-0 flex-1 flex-col gap-11 sm:px-6 xl:max-w-[680px]">
					<header className="flex flex-col">
						<div className="mb-3 flex items-center gap-1.5 text-[13px] font-medium text-tertiary">
							<Link to={page.tab.slug} className="hover:text-primary">
								{page.tab.name}
							</Link>
							<span aria-hidden>›</span>
							<span className="truncate text-secondary">{groupName}</span>
						</div>
						<h1
							className={cn(
								"text-[30px] font-semibold leading-[1.2] tracking-[-0.022em] text-primary",
								isWebhook && "font-mono text-[24px] tracking-[-0.01em]",
							)}
						>
							{operation.title}
						</h1>
						{operation.deprecated && (
							<Badge variant="amber" size="code" className="mt-3 self-start">
								{t("apiReference.deprecated", "Deprecated")}
							</Badge>
						)}
						{operation.description && (
							<InlineMarkdown
								text={operation.description}
								className="mt-3 text-[16px] leading-[1.65] text-secondary"
							/>
						)}
						{!isWebhook && (
							<div className="mt-6">
								<UrlBar copyValue={url}>
									<MethodBadge operation={operation} />
									<span className="flex items-center">
										<PathText
											path={`${serverPathPrefix(page.servers[0])}${operation.path}`}
										/>
									</span>
								</UrlBar>
							</div>
						)}
					</header>

					{samplesCard && <div className="xl:hidden">{samplesCard}</div>}

					{operation.security.length > 0 && (
						<Section title={t("apiReference.authorization", "Authorization")}>
							<AuthFields auth={operation.security} />
						</Section>
					)}

					{params.length > 0 && (
						<Section
							title={
								isWebhook
									? t("apiReference.headers", "Headers")
									: t("apiReference.parameters", "Parameters")
							}
						>
							<ParamFields params={params} showLocation={!isWebhook} />
						</Section>
					)}

					{operation.body && (
						<Section
							title={t("apiReference.body", "Body")}
							aside={
								<span className="font-mono text-[12px] text-tertiary">
									{operation.body.contentType}
								</span>
							}
						>
							{operation.body.description && (
								<InlineMarkdown
									text={operation.body.description}
									className="pt-4 text-[14px] leading-[1.6] text-secondary"
								/>
							)}
							{operation.body.schema.children?.length ? (
								<SchemaFields fields={operation.body.schema.children} />
							) : (
								<div className="py-4">
									<Badge variant="light-gray" size="code">
										{schemaTypeLabel(operation.body.schema)}
									</Badge>
								</div>
							)}
						</Section>
					)}

					{payloadCard && <div className="xl:hidden">{payloadCard}</div>}

					<ResponsesSection
						operation={operation}
						status={status}
						onStatusChange={setStatus}
					/>

					{responseCard && <div className="xl:hidden">{responseCard}</div>}

					<nav className="grid grid-cols-2 gap-3 pt-2">
						{page.previous ? (
							<PagerLink
								to={page.previous.href}
								arrow="←"
								title={page.previous.title}
							/>
						) : (
							<span />
						)}
						{page.next && (
							<PagerLink
								to={page.next.href}
								arrow="→"
								title={page.next.title}
								alignEnd
							/>
						)}
					</nav>
				</article>

				<aside className="hidden flex-none flex-col gap-4 xl:sticky xl:top-[calc(1.5rem+var(--docs-tabs-h,0px))] xl:flex xl:w-[27rem]">
					{samplesCard}
					{payloadCard}
					{responseCard}
				</aside>
			</div>
		</div>
	)
}

const PagerLink: FC<{
	to: string
	arrow: string
	title: string
	alignEnd?: boolean
}> = ({ to, arrow, title, alignEnd }) => {
	const { Link } = useApiReferenceEnv()
	return (
		<Link
			to={to}
			className={cn(
				"flex min-w-0 flex-col gap-1 rounded-xl px-4 py-3.5 ring-1 ring-black/[0.08] hover:bg-gray-50 dark:ring-white/[0.08] dark:hover:bg-white/[0.03]",
				alignEnd && "items-end text-right",
			)}
		>
			<span className="text-[12px] text-tertiary">{arrow}</span>
			<span className="w-full truncate text-[14px] font-medium text-primary">
				{title}
			</span>
		</Link>
	)
}

const ResponsesSection: FC<{
	operation: OperationDoc
	status: string | null
	onStatusChange: (status: string) => void
}> = ({ operation, status, onStatusChange }) => {
	const { t } = useApiReferenceEnv()
	const selected = operation.responses.find((r) => r.status === status)
	if (operation.responses.length === 0) return null

	return (
		<Section
			title={
				operation.kind === "webhook"
					? t("apiReference.yourResponse", "Your response")
					: t("apiReference.response", "Response")
			}
			aside={
				<>
					{selected?.contentType && (
						<span className="font-mono text-[12px] text-tertiary">
							{selected.contentType}
						</span>
					)}
					<StatusPicker
						responses={operation.responses}
						status={status}
						onStatusChange={onStatusChange}
					/>
				</>
			}
		>
			{selected?.description && (
				<InlineMarkdown
					text={selected.description}
					className="pt-4 text-[14px] leading-[1.6] text-secondary"
				/>
			)}
			{selected?.schema?.children?.length ? (
				<SchemaFields fields={selected.schema.children} />
			) : selected?.schema ? (
				<div className="py-4">
					<Badge variant="light-gray" size="code">
						{schemaTypeLabel(selected.schema)}
					</Badge>
				</div>
			) : null}
		</Section>
	)
}

const StatusPicker: FC<{
	responses: ResponseDoc[]
	status: string | null
	onStatusChange: (status: string) => void
}> = ({ responses, status, onStatusChange }) => {
	const { t, monoClassName } = useApiReferenceEnv()
	const pointer = usePointerClass()
	if (responses.length < 2) {
		return (
			<span className="font-mono text-[12.5px] text-secondary">{status}</span>
		)
	}
	return (
		<DropdownMenu
			trigger={
				<Button
					variant="ghost"
					size="sm"
					iconRight={ChevronsUpDown}
					aria-label={t("apiReference.response", "Response")}
					className={cn("h-7 rounded-md px-2 font-mono text-[12.5px]", pointer)}
				>
					{status}
				</Button>
			}
			contentProps={{ align: "end" }}
			className={cn("w-auto min-w-[7rem]", monoClassName)}
		>
			{responses.map((response) => (
				<DropdownMenuItem
					key={response.status}
					onSelect={() => onStatusChange(response.status)}
					IconRight={response.status === status ? Check : undefined}
					className={cn("gap-3 font-mono text-[12.5px]", pointer)}
				>
					{response.status}
				</DropdownMenuItem>
			))}
		</DropdownMenu>
	)
}

/** Remembers the reader's language across endpoints, like the references they know. */
function useSampleLanguage() {
	const [language, setLanguage] = useState<keyof CodeSamples>("curl")
	useEffect(() => {
		try {
			const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
			if (SAMPLE_LANGUAGES.some((option) => option.id === stored)) {
				setLanguage(stored as keyof CodeSamples)
			}
		} catch {
			// Storage can be blocked; the default stays.
		}
	}, [])
	const choose = (next: keyof CodeSamples) => {
		setLanguage(next)
		try {
			window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next)
		} catch {
			// Storage can be blocked; the choice still holds for this page.
		}
	}
	return [language, choose] as const
}

const SamplesCard: FC<{ samples: CodeSamples; operation: OperationDoc }> = ({
	samples,
	operation,
}) => {
	const { t } = useApiReferenceEnv()
	const pointer = usePointerClass()
	const [language, setLanguage] = useSampleLanguage()
	const current = SAMPLE_LANGUAGES.find((option) => option.id === language)
	return (
		<CodeCard
			header={
				<>
					<MethodBadge operation={operation} />
					<span className="truncate font-mono text-[12.5px] text-secondary">
						{operation.path}
					</span>
				</>
			}
			actions={
				<>
					<DropdownMenu
						trigger={
							<Button
								variant="ghost"
								size="sm"
								iconRight={ChevronsUpDown}
								aria-label={t("apiReference.request", "Request")}
								className={cn("h-7 rounded-md px-2 text-[12.5px]", pointer)}
							>
								{current?.label}
							</Button>
						}
						contentProps={{ align: "end" }}
						className="w-40"
					>
						{SAMPLE_LANGUAGES.map((option) => (
							<DropdownMenuItem
								key={option.id}
								onSelect={() => setLanguage(option.id)}
								IconRight={option.id === language ? Check : undefined}
								className={cn("text-[13px]", pointer)}
							>
								{option.label}
							</DropdownMenuItem>
						))}
					</DropdownMenu>
					<CopyButton
						value={samples[language]}
						className="text-tertiary hover:text-primary"
					/>
				</>
			}
		>
			<HighlightedCode
				code={samples[language]}
				language={SAMPLE_HIGHLIGHT[language]}
			/>
		</CodeCard>
	)
}

const ResponseExampleCard: FC<{
	examples: ResponseDoc[]
	status: string | null
	onStatusChange: (status: string) => void
}> = ({ examples, status, onStatusChange }) => {
	const pointer = usePointerClass()
	const shown = examples.find((r) => r.status === status) ?? examples[0]
	if (!shown?.example) return null
	return (
		<CodeCard
			header={
				<div role="tablist" className="-ml-1.5 flex items-center gap-0.5">
					{examples.map((response) => (
						<Button
							key={response.status}
							variant="ghost"
							size="sm"
							role="tab"
							aria-selected={response.status === shown.status}
							onClick={() => onStatusChange(response.status)}
							className={cn(
								"h-7 rounded-md px-2 font-mono text-[12.5px]",
								pointer,
								response.status === shown.status &&
									"bg-black/[0.05] text-primary hover:bg-black/[0.05] dark:bg-white/[0.08] dark:hover:bg-white/[0.08]",
							)}
						>
							{response.status}
						</Button>
					))}
				</div>
			}
			actions={
				<CopyButton
					value={shown.example}
					className="text-tertiary hover:text-primary"
				/>
			}
		>
			<HighlightedCode
				code={shown.example}
				language={exampleLanguage(shown.example)}
				className="max-h-[32rem]"
			/>
		</CodeCard>
	)
}

/** A code panel inset in a tinted frame; the frame's header holds the label and actions. */
const CodeCard: FC<{
	header: ReactNode
	actions?: ReactNode
	children: ReactNode
}> = ({ header, actions, children }) => (
	<div className="flex flex-col rounded-xl bg-gray-50 p-1 ring-1 ring-black/[0.07] dark:bg-white/[0.03] dark:ring-white/[0.08]">
		<div className="flex h-9 items-center justify-between gap-2 pl-2.5 pr-0.5">
			<div className="flex min-w-0 items-center gap-2">{header}</div>
			<div className="flex flex-none items-center gap-0.5">{actions}</div>
		</div>
		<div className="overflow-hidden rounded-lg bg-white ring-1 ring-black/[0.06] dark:bg-black/30 dark:ring-white/[0.06]">
			{children}
		</div>
	</div>
)

const OverviewView: FC<{
	page: PortalApiReferencePage
	className?: string
}> = ({ page, className }) => {
	const { t, Link, monoClassName } = useApiReferenceEnv()
	return (
		<div
			className={cn(
				"mx-auto flex w-full min-w-0 max-w-[1180px] flex-col pb-16 pt-6",
				monoClassName,
				className,
			)}
		>
			<div className="flex w-full max-w-[680px] flex-col gap-11 sm:px-6">
				<header className="flex flex-col">
					<h1 className="text-[30px] font-semibold leading-[1.2] tracking-[-0.022em] text-primary">
						{page.tab.name}
					</h1>
					<div className="mt-3 flex items-center gap-2 text-[14px] text-tertiary">
						<span>{page.title}</span>
						{page.apiVersion && (
							<Badge variant="light-gray" size="code">
								{page.apiVersion}
							</Badge>
						)}
					</div>
					{page.description && (
						<InlineMarkdown
							text={page.description}
							className="mt-4 text-[16px] leading-[1.65] text-secondary"
						/>
					)}
				</header>
				{page.servers[0] && (
					<Section title={t("apiReference.baseUrl", "Base URL")}>
						<div className="pt-4">
							<UrlBar copyValue={page.servers[0]}>
								<span className="pl-0.5">{page.servers[0]}</span>
							</UrlBar>
						</div>
					</Section>
				)}
				{page.auth.length > 0 && (
					<Section title={t("apiReference.authorization", "Authorization")}>
						<AuthFields auth={page.auth} />
					</Section>
				)}
				<section className="grid gap-3 sm:grid-cols-2">
					{page.groups.map((group) => {
						const first = group.items[0]
						return (
							<Link
								key={group.slug}
								to={
									first
										? `${page.tab.slug}/${group.slug}/${first.slug}`
										: page.tab.slug
								}
								className="flex items-center justify-between gap-3 rounded-xl px-4 py-3.5 ring-1 ring-black/[0.08] hover:bg-gray-50 dark:ring-white/[0.08] dark:hover:bg-white/[0.03]"
							>
								<span className="truncate text-[14px] font-medium text-primary">
									{group.name ??
										t("apiReference.webhookEvents", "Webhook events")}
								</span>
								<Badge variant="light-gray" size="code">
									{group.items.length}
								</Badge>
							</Link>
						)
					})}
				</section>
			</div>
		</div>
	)
}
